import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { Switch } from "@headlessui/react";
import clsx from "clsx";
import { useId } from "react";

export type SwitchLabelPosition = "right" | "left" | "bottom";

function HeadlessBooleanSwitch({
  checked,
  onChange,
  disabled,
  className,
  id,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
}) {
  return (
    <Switch
      id={id}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className={clsx(
        "group inline-flex h-6 w-11 shrink-0 items-center rounded-full transition focus:outline-hidden",
        checked ? "bg-blue-600" : "bg-gray-200",
        disabled && "cursor-not-allowed opacity-50",
        className,
      )}
    >
      <span
        className={clsx(
          "size-5 rounded-full bg-white transition",
          checked ? "translate-x-[22px]" : "translate-x-px",
        )}
      />
    </Switch>
  );
}

export interface SwitchWithLabelProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: React.ReactNode;
  /** @default "right" */
  labelPosition?: SwitchLabelPosition;
  disabled?: boolean;
  className?: string;
  switchClassName?: string;
  id?: string;
}

export const SwitchWithLabel = ({
  checked,
  onCheckedChange,
  label,
  labelPosition = "right",
  disabled,
  className,
  switchClassName,
  id: idProp,
}: SwitchWithLabelProps) => {
  const reactId = useId();
  const switchId = idProp ?? `switch-${reactId}`;

  const toggle = (
    <HeadlessBooleanSwitch
      id={switchId}
      checked={checked}
      onChange={onCheckedChange}
      disabled={disabled}
      className={switchClassName}
    />
  );

  const labelNode = (
    <span className="text-sm font-medium text-gray-800">
      <label htmlFor={switchId} className={disabled ? "cursor-not-allowed" : "cursor-pointer"}>
        {label}
      </label>
    </span>
  );

  if (labelPosition === "left") {
    return (
      <div className={clsx("flex flex-row items-center justify-between gap-3", className)}>
        {labelNode}
        {toggle}
      </div>
    );
  }

  if (labelPosition === "bottom") {
    return (
      <div className={clsx("flex flex-col items-start justify-between gap-2", className)}>
        {toggle}
        {labelNode}
      </div>
    );
  }

  return (
    <div className={clsx("flex flex-row items-center justify-between gap-3", className)}>
      {toggle}
      {labelNode}
    </div>
  );
};

interface StyledSwitchProps<FieldsType extends FieldValues> {
  name: Path<FieldsType>;
  control: Control<FieldsType>;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  disabled?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

const StyledSwitch = <FieldsType extends FieldValues>({
  name,
  control,
  className,
  disabled,
  onCheckedChange,
}: StyledSwitchProps<FieldsType>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <HeadlessBooleanSwitch
          checked={!!field.value}
          onChange={(checked: boolean) => {
            field.onChange(checked);
            onCheckedChange?.(checked);
          }}
          disabled={disabled}
          className={className}
        />
      )}
    />
  );
};

export default StyledSwitch;
